import { RenderFlags } from "./RenderFlags";
import { logger } from "../../logger/Logger";
import { getGlobal, isMobileDevice } from "../../compat";
import { GroundFlags } from "../render/GroundFlags";
import { USE_OUT_OF_CORE_TILE_MANAGER, USE_WBOIT } from "../globals";
import * as shadow from "../render/ShadowMap";
import { ProgressState } from "../../application/ProgressState";
import { renderBudgetManager } from "../../application/RenderBudgetManager";
import * as et from "../../application/EventTypes";

const ENABLE_DEBUG = getGlobal().ENABLE_DEBUG;
const ENABLE_DEBUG_RCS = getGlobal().ENABLE_DEBUG_RCS;

const NOTHING_DRAWN = 0;
const MODEL_DRAWN = 1;
const BACKGROUND_DRAWN = 2;
const OVERLAY_DRAWN = 4;
const REFLECTION_DRAWN = 8;
const ALL_DRAWN = 15;


// various types of rendering
export const RENDERMODE_FULL = 0;
export const RENDERMODE_PROGRESSIVE = 1;
export const RENDERMODE_SILENT = 2;

// command states
export const CMD_NORMAL_SEQUENCE = 0;
export const CMD_DO_AFTER = 1;
export const CMD_ALWAYS_DO = 2;

/**
 * Helper function to render main-target render layers.
 * @param {Object} viewer - The viewer instance
 * @param {Array} layers - Array of render layers to render
 */
function renderMainRenderLayers(viewer, layers) {
  const renderer = viewer.renderer();

  for (const layer of layers) {
    // Render scene if present
    if (layer.scene && layer.scene.children.length !== 0) {
      renderer.renderScenePart(layer.scene, true, true, true);
    }

    // Render models if present
    if (layer.models && layer.models.length > 0) {
      renderer.renderLayerModels(layer.models);
    }

    // Clear depth after rendering if requested
    if (layer.clearDepthAfter) {
      renderer.clearMainDepth();
    }
  }
}

function cmdBeginScene() {

  const viewer = this.viewer;
  const _modelQueue = viewer.modelQueue();

  if (this.signalProgressByRendering)
  this.viewer.signalProgress(0, ProgressState.RENDERING); //zero out the progress bar for when rendering begins

  if (this.renderType === RENDERMODE_PROGRESSIVE) {
    //Measure actual frame time between two consecutive initial frames.
    //This is used to correct measured per-scene times to what they actually take
    //once the async processing of the graphics thread is taken into account.
    if (this.lastBeginFrameTimeStamp > 0) {
      const delta = this.highResTimeStamp - this.lastBeginFrameTimeStamp;
      if (USE_OUT_OF_CORE_TILE_MANAGER) {
        renderBudgetManager.updateFrameTime(viewer, _modelQueue.isDone(), this._usedFrameTime, delta, this.TARGET_FRAME_TIME * this.frameTimeFraction);
      } else {
        this.beginFrameAvg = 0.75 * this.beginFrameAvg + 0.25 * delta;

        // Finally we adjust the time budget based on the desired frame time and the averaged ratio
        this.updateTargetFrameBudget();
      }
    }
    this.lastBeginFrameTimeStamp = this.highResTimeStamp;
  }

  // clear the color and depth targets
  const clear = this.getParam("BeginScene.clear");

  // We call beginScene with the empty THREE.Scene just to update camera and lights uniforms
  viewer.renderer().beginScene(viewer.emptyScene, viewer.camera, viewer.lightsOn ? viewer.lights : viewer.no_lights, clear);

  if (clear) {

    // Render main-target layers with renderOrder < 0 (before main scene)
    const mainLayers = viewer.getRenderLayerManager().getMainPassRenderLayers();
    if (mainLayers.before.length > 0) {
      renderMainRenderLayers(viewer, mainLayers.before);
    }

    this.screenDrawn |= BACKGROUND_DRAWN;
    _modelQueue.onClear();
  }

  // Check if the camera changed, and if so, signal.
  if (this.getParam("BeginScene.signalCameraChanged")) {
    // Tells view cube to update, for example.
    viewer.api.dispatchEvent(viewer.cameraChangedEvent);
  }

  return false;
}

function cmdMainRender() {
  let viewer = this.viewer;
  let _modelQueue = viewer.modelQueue();

  //This is done in _tick() instead
  //viewer._updateLastRenderTime();

  if (!_modelQueue.isEmpty() && !_modelQueue.isDone()) {

    this.screenDrawn |= MODEL_DRAWN;

    // It's important that we only scale the budget during the normal phase, since the error would multiply over the phases
    _modelQueue.setRenderBudgetScalingEnabled(this.phase === RenderFlags.RENDER_NORMAL);

    // When using WBOIT, only progressive frames after the first should
    // redraw transparent frags, and only during the normal phase.
    _modelQueue.setTransparentsNeedRedraw(
      USE_WBOIT &&
      // Don't reset while drawing ghosting, etc.
      this.phase === RenderFlags.RENDER_NORMAL &&
      // Don't reset on first progressive frame.
      this.renderType === RENDERMODE_PROGRESSIVE &&
      this.tickCount > 0);

    //Render some geometry with the current render mode (highlighted, normal, or ghosted)
    this.frameRemaining = _modelQueue.renderSome(viewer.renderSomeCallback.bind(viewer), this.frameRemaining,
    this.frameTimeScalingFactorAvg);

    // WebGPU renderer requires flushing the command queue after each progressive frame.
    if (viewer.isWebGPU()) {
      viewer.glrenderer().flushCommandQueue();
      if (viewer.getSheetRenderer()) {
        viewer.getSheetRenderer().glRenderer.flushCommandQueue();
      }
    }

    // TODO - cmdMainRender gets used by a number of systems - what sort of progress should really happen here?
    if (this.signalProgressByRendering) {
      viewer.signalProgress(100.0 * _modelQueue.getRenderProgress(), ProgressState.RENDERING);
      if (ENABLE_DEBUG_RCS) {
        console.log("  %%% percent done " + 100 * _modelQueue.getRenderProgress());
      }
    }
  }
  // Halt rendering if halt frame is set and matches the current tick count. This freezes progressive rendering at
  // the Nth intermediate frame and is intended for inspecting image completeness/convergence at a specific frame.
  if (this.progressiveHaltFrame > 0 && this.tickCount >= this.progressiveHaltFrame - 1) {
    return false;
  }

  // if there is time left, continue on (return false), else return true, which means "stop for now"
  return !_modelQueue.isDone();
}

// render sectioning, if any, and any additional buffers needed, such as ID.
function cmdSceneAfterRender() {

  const viewer = this.viewer;

  this.phase = RenderFlags.RENDER_FINISHED;

  // Render main-target layers with renderOrder > 0 (after main scene)
  const mainLayers = viewer.getRenderLayerManager().getMainPassRenderLayers();
  if (mainLayers.after.length > 0) {
    renderMainRenderLayers(viewer, mainLayers.after);
  }

  // TODO: bad, renderScenePart does not return the amount of time used to render. It should, so we know the remaining budget.
  // TODO: to be honest, we should actually do a performance.now() at the beginning of any command list set, and
  // use *that* to track the time truly remaining. highResTimeStamp that is passed in is not trustworthy. But there's also the
  // "average batch time" that gets set, to minimize flicker. A creaky system that works, mostly.
  return false;
}

function cmdRenderOverlays() {

  const viewer = this.viewer;
  const _modelQueue = viewer.modelQueue();

  // Render selection highlight / pivot / HUD overlays and other overlay geometry
  // This is stuff that goes into the separate overlay render buffer. It does rely on the z-buffer being properly populated,
  // so is normally rendered after the beauty pass (or highlighting pass) is performed. As such, we need to rerender it on
  // every progressive pass.

  // If there was an overlay dirty (i.e., someone hovered over something in the middle of a progressive render), note that the
  // overlay must now be drawn from here on out.
  if (viewer.isOverlayDirty()) {
    // avoid having successive passes
    viewer.clearOverlayDirtyFlag();
    this.drawOverlay = true;
  }

  // draw if needed
  if (this.drawOverlay) {

    // If there is geometry, and we're done rendering it, OR we need to always render the overlay while moving, make the overlay
    if (!_modelQueue.isEmpty() && _modelQueue.isDone() || viewer.showOverlaysWhileMoving) {
      viewer.renderOverlays();
      this.screenDrawn |= OVERLAY_DRAWN;
    } else {
      // overlay update not needed (no geometry, or to be done only at end): clear once, and turn off drawing it since we need to clear only once.
      viewer.renderer().clearAllOverlays();
      this.drawOverlay = false;
    }
  }

  return false;
}

// if we're fading in the rollover highlight, we just need to compose the final frame.
// This command forces PostAndPresent to happen.
function cmdForcePresent() {
  this.screenDrawn |= ALL_DRAWN;
}

function cmdPostAndPresent() {

  const viewer = this.viewer;

  //Run post-processing and present to the front buffer
  if (this.screenDrawn && (
  // present if we're done rendering, or if progressive and this is a displayable frame
  this.phase === RenderFlags.RENDER_FINISHED || this.tickCount % this.frameDisplayRate === 0)) {

    // Skip AO if we clear the screen and nothing else was drawn, or if
    // it was disabled when we created the command list.
    let skipAO = !this.getParam("PostAndPresent.performAO") ||
    (this.screenDrawn & (BACKGROUND_DRAWN | MODEL_DRAWN)) === BACKGROUND_DRAWN;
    // present image
    viewer.renderer().composeFinalFrame(skipAO);
    this.screenHasChanged = true;
    // reset
    this.screenDrawn = NOTHING_DRAWN;

    viewer.api.dispatchEvent({ type: et.RENDER_PRESENTED_EVENT });
  }

  return false; // TODO - could actually measure time at this point
}

function cmdFinishAllRendering() {
  // in case some system is querying the phase
  this.phase = RenderFlags.RENDER_FINISHED;

  return false;
}

// Smooth navigation overrides
// TODO - I don't really like toggle SAO off and on during a single tick, it is a little
// costly (how much?), but it's the safest option.
function cmdSuppressAO() {

  const viewer = this.viewer;

  if (ENABLE_DEBUG) {
    if (viewer.renderer().getAOEnabled() === false) {
      // AO should be on and we should be suppressing it.
      logger.error("AO should be on at this point!");
    }
  }
  viewer.renderer().setAOEnabled(false);
  return false;
}
function cmdRestoreAO() {

  const viewer = this.viewer;

  if (ENABLE_DEBUG) {
    if (viewer.renderer().getAOEnabled() === true) {
      // AO should be off and we should be restoring it.
      logger.error("AO should be off at this point!");
    }
  }
  viewer.renderer().setAOEnabled(true);
  return false;
}

// Here's the system:
// If ground shadow is done - well, that's easy, just blit it before beauty pass
// If not done
//     If we are doing a full render
//     Render the whole shadow first (possibly tick limited), blit it to screen, then continue to beauty pass
//     Else we are doing progressive
//        if this is the first frame:
//           if the number of objects in the scene is low (10?),
//              render the drop shadow, figuring we can rendering the rest of the scene in a single frame
//           else
//              don't bother rendering anything in later ticks (we used to waste time rendering a few each tick)
//        if this is a later frame:
//           render just the beauty pass, until done. Don't bother with the shadow now, as it won't get used.
//        When we get to the end of progressive:
//           If needed, render the ground shadow until done. Once done, signal that a re-render is needed.
function cmdGenerateGroundShadow() {

  const viewer = this.viewer;

  // three cases: full render, quick out for progressive, continue as possible for progressive.
  if (this.renderType === RENDERMODE_PROGRESSIVE) {
    if (this.getParam("GenerateGroundShadow.afterBeauty")) {
      // Rendering the ground shadow after all progressive rendering is done. Signal redraw if it finishes.
      this.frameRemaining = viewer.groundShadow().prepareGroundShadow(viewer.modelQueue(), 0, this.frameRemaining);
      // was this the call that rendered it fully?
      if (viewer.groundShadow().getStatus() === GroundFlags.GROUND_RENDERED) {
        // Do we need to rerender? This needs to happen if we're not using reflection insertion.
        // TODO: someday perhaps make ground shadows more "full featured" and merge behind, like ground reflections do?
        if (this.getParam("GenerateGroundShadow.signalRedraw")) {
          viewer.requestSilentRender();
          if (ENABLE_DEBUG_RCS) {
            console.log(" $$$$ SIGNAL FULL GROUND SHADOW REDRAW");
          }
          // don't need to continue, since we know we need to fully redraw on next tick
          return true; // TODO could signal abort rest of command stream, since we know we invalidate. It's just a bit inefficient otherwise.
        }
        // note for ground reflection, so it can decide on deferred silent rendering.
        this.groundShadowInPost = true;
      }
    } else {
      // If this is the first frame, try to render the drop shadow in a small amount of time.
      // Else, don't waste time on the drop shadow.
      if (this.tickCount === 0) {
        // render 10 objects TODO - expose 10 as some other number?
        //this.frameRemaining =
        viewer.groundShadow().prepareGroundShadow(viewer.modelQueue(), 10, USE_OUT_OF_CORE_TILE_MANAGER ? 3 : undefined);
        // TODO or this way, which does possibly give flicker:
        //this.frameRemaining = viewer.groundShadow().prepareGroundShadow(viewer.modelQueue(), this.frameRemaining, this.INITIAL_GROUND_SHADOW);
        //const minRemaining = this.frameBudget * (1-this.INITIAL_GROUND_SHADOW);
        //if ( this.frameRemaining < minRemaining ) {
        //     this.frameRemaining = minRemaining;
        //}
      }
    }
  } else {
    // full render, just do it fully.
    this.frameRemaining = viewer.groundShadow().prepareGroundShadow(viewer.modelQueue(), 0, this.frameRemaining);
  }

  // rendering can continue if there's time left
  return this.frameRemaining < 0 && viewer.groundShadow().getStatus() === GroundFlags.GROUND_UNFINISHED;
}

function cmdBlitGroundShadow() {
  const viewer = this.viewer;

  //Render the ground shadow after screen clear
  if (viewer.groundShadow().getStatus() !== GroundFlags.GROUND_UNFINISHED)
  viewer.renderGroundShadow();
  return false;
}

function cmdGenerateGroundReflection() {

  const viewer = this.viewer;
  const _groundReflection = viewer.groundReflection();

  // three cases: full render, quick out for progressive, continue as possible for progressive.
  if (this.renderType === RENDERMODE_PROGRESSIVE) {
    // is this pass happening after the beauty pass is completed?
    if (this.getParam("GenerateGroundReflection.afterBeauty")) {
      // Rendering the ground reflection after all progressive rendering is done.
      this.frameRemaining = _groundReflection.prepareGroundReflection(viewer.groundShadow(), viewer, false, 0, this.frameRemaining);
      // was this the call that rendered it fully?
      if (_groundReflection.getStatus() === GroundFlags.GROUND_RENDERED) {
        this.screenDrawn |= REFLECTION_DRAWN;
        // If we're done, we should also check to see if a silent render is needed for ground shadows.
        // If ground shadows were finished in these post-render passes (rare - only on animation or explode,
        // for complex scenes), transparent objects in the scene will not show the shadows properly through
        // their transparent objects, LMV-2508.
        // TODO - nicer still would be to see if the scene actually has any transparent objects. If not,
        // then we don't need this separate re-render.
        // TODO Also, note this isn't a perfect system: in practice you really want to have the ground reflection
        // entirely done before rendering atop it, so that what is seen through transparent objects is fully
        // folded in. However, this problem is much less obvious in the scenes tested - missing ground shadows
        // are more obvious.
        if (this.groundShadowInPost && viewer.matman().hasTransparentMaterial()) {
          viewer.requestDeferredSilentRender();
        }
      }
    } else {
      // If this is the first frame, try to render the reflection in a small amount of time.
      // Else, don't waste time on the reflection.
      if (this.tickCount === 0) {
        // render 10 objects TODO - expose 10 as some other number? Or use a budget? Or...?
        //this.frameRemaining =
        _groundReflection.prepareGroundReflection(viewer.groundShadow(), viewer, true, 10, USE_OUT_OF_CORE_TILE_MANAGER ? 5 : undefined);
        // TODO or this way, which does possibly give flicker:
        //this.frameRemaining = _groundReflection.prepareGroundReflection(_modelQueue, this.frameRemaining, this.INITIAL_GROUND_SHADOW);
        //const minRemaining = this.frameBudget * (1-this.INITIAL_GROUND_SHADOW);
        //if ( this.frameRemaining < minRemaining ) {
        //     this.frameRemaining = minRemaining;
        //}
      }
    }
  } else {
    // full render, just do it fully.
    this.frameRemaining = _groundReflection.prepareGroundReflection(viewer.groundShadow(), viewer, false, 0, this.frameRemaining);
  }

  // rendering can continue if there's time left, or if we actually finished display and should present, even though we're out of time.
  // TODO we could revise commands to be of "takes time" and "doesn't take time", so that we abort if and only if we're out of time
  // and hit a "takes time" command.
  return this.frameRemaining < 0 && _groundReflection.getStatus() === GroundFlags.GROUND_UNFINISHED;
}

/**
 * Progressive update of the shadow map:
 *
 *   a) For small models that can be rendered within a single frame, the shadow map will always be rendered first,
 *      so that shadows will not flicker on and off during animations, on scene changes, or when changing the light direction.
 *   b) For large models, seeing something is more important than shadows. Therefore, we render without shadows
 *      first and only do work on the shadow map if everything else is finished.
 *
 *  Whether we take a) or b) is determined on-the-fly: We use a) if we succeed updating the whole ShadowMap
 *  within a single frame time budget.
 */
function cmdUpdateShadowMap() {

  let viewer = this.viewer;
  let _shadowMaps = viewer.shadowMaps();

  // We are either starting an update of the shadow map, or are continuing to render it in this tick.

  // This section is always entered in the first frame if the shadow map is not available yet.
  if (_shadowMaps.state === shadow.SHADOWMAP_NEEDS_UPDATE) {

    // start shadow map update. This call may end in two ways:
    //  - In case a), the shadowmap could already be finished within the startUpdate() call. Therefore, the
    //    shadow map will already be available and will be used in this frame.
    //    In this case, there is nothing more to do and all subsequent calls to updateShadowMap will
    //    do nothing.
    //  - in case b), the shadow map is not available. In this case, we first wait until the rendering
    //    without shadows is finished. (see next section)
    this.frameRemaining = _shadowMaps.startUpdate(viewer.modelQueue(), this.frameRemaining, viewer.camera, viewer.getShadowLightDirection(), viewer.matman());

  } else if (_shadowMaps.state === shadow.SHADOWMAP_INCOMPLETE) {

    // continue shadow map update as long as we have time
    this.frameRemaining = _shadowMaps.continueUpdate(viewer.modelQueue(), this.frameRemaining, viewer.matman());

    // if we're done and this is a progressive render, then this shadow generation is happening at the end.
    // In such a case we need to re-render (similar to ground shadows and reflections).
    if (_shadowMaps.state === shadow.SHADOWMAP_VALID) {

      // TODO - may wish to make this a deferred silent render, so that reflection etc. is completed, then shadows come in later.
      viewer.requestSilentRender();
      if (ENABLE_DEBUG_RCS) {
        console.log(" $$$$ SIGNAL FULL SHADOW MAP REDRAW");
      }
      // don't need to continue, since we know we need to fully redraw on next tick
      return true; // TODO could signal abort rest of command stream, since we know we invalidate. It's just a bit inefficient otherwise.
    }
  }
  return this.frameRemaining < 0.0 && _shadowMaps.state !== shadow.SHADOWMAP_VALID;
}

function cmdResetShadowMap() {
  this.viewer.shadowMaps().state = shadow.SHADOWMAP_NEEDS_UPDATE;
}


function cmdBeginPhase() {
  // If nothing is highlighted just skip the highlighted phase
  this.phase = this.getParam("BeginPhase.phase");
  if (ENABLE_DEBUG_RCS) {
    console.log("     render phase is now " + this.phase);
  }

  // Start rendering the scene by resetting the rendering queue.
  // This sets up the view frustum intersector and begins scene iteration.
  const viewer = this.viewer;
  viewer.modelQueue().reset(viewer.camera, this.phase, this.getParam("BeginPhase.moved"), viewer.matman().getCutPlanes(), viewer.cutplanesHideInteriorEnabled());

  return false;
}


function cmdSignalProcessingDone() {
  //if (this.signalProgressByRendering)
  this.viewer.signalProgress(100.0, ProgressState.RENDERING);
}

function cmdSignalRedraw() {
  this.viewer.requestSilentRender();
  return false;
}
function cmdFinishedFullRender() {
  this.finishedFullRender = true;
  return false;
}

// The render command system is what actually does the render. The idea here is that each tick() checks if anything causes a new
// render. If so, then we make a new list of commands to perform, then start performing them. For a full render without interruptions,
// this is overkill - we could just lockstep execute them all. Where the command list comes into its own is that it can be continued.
// For progressive rendering we want
// Rather than pepper the rendering sequence of the code with lots of "if" statements that
export class RenderCommandSystem {

  constructor(viewerImpl)
  {
    this.viewer = viewerImpl;

    //Frame time cutoffs in milliseconds. We target the middle value,
    //but adjust the CPU-side work in the give min/max range
    //once we measure actual frame times (including async GPU work, system load, etc).
    //NOTE: These are doubled for mobile devices at construction time (end of this file).
    this.MAX_FRAME_BUDGET = 1000 / 15;
    this.TARGET_FRAME_TIME = 1000 / 30;
    this.MIN_FRAME_BUDGET = 0.1; // This is the minimum time we will spend drawing and
    // is only indirectly related to the fastest frame rate.  (0.1 corresponds to at least a single batch
    // submitted as fast as possible, i.e. the clamped minimum time)

    this.targetFrameBudget = this.TARGET_FRAME_TIME;

    // How many ticks pass in between updates. Make this half as many for mobile because the frame budget is doubled.
    // 1 means that we display every frame
    this.frameDisplayRate = 5;
    if (isMobileDevice()) {
      this.MAX_FRAME_BUDGET *= 2; // Increase to match TARGET_FRAME_TIME
      this.TARGET_FRAME_TIME *= 2; // GPUs are slower on mobile use a longer target frame time
      this.targetFrameBudget /= 2; // Even though the target's doubled, start the budget smaller and have it work up to the target (ask Cleve)
      this.frameDisplayRate /= 2; // since time per tick is doubled (in the long run), halve the number of ticks to give the same wall-clock delay interval
    }
    // How much time between checks on a full frame for any interrupt signal.
    this.interruptBudget = 1e10;

    this.highResTimeStamp = -1;
    // did something get rendered that would change the screen (almost always true when rendering occurs)?
    this.screenDrawn = NOTHING_DRAWN;
    // did a Present get done?
    this.screenHasChanged = false;
    // how much time we are given to render things during each execution, in ms.
    this.frameBudget = 0;
    // how much of our frame budget is already used up?
    this._usedFrameTime = 0;
    // Smoothed scaling factor for the frame time budget
    this.frameTimeScalingFactorAvg = 1;
    // Fraction of the total frame time that has been allocated to this viewer instance
    this.frameTimeFraction = 1;
    // how much time we have left to render stuff during this tick(), in ms.
    this.frameRemaining = 0;
    // what type of render is happening currently
    this.phase = RenderFlags.RENDER_NORMAL;
    // show the amount of the scene rendered. TODO this doesn't really work right with ghosting or shadow mapping on, as those also affect it.
    this.signalProgressByRendering = false;

    // How many ticks have executed the current command list. Good for knowing if we're on the first frame (tick 0).
    this.tickCount = 0;
    // average time spent rendering a tick() TODO - needs to be revisited and thought through: if a batch is not loaded, it displays really fast!
    this.beginFrameAvg = 0;
    // exactly what it says, the time stamp passed in on the previous tick().
    this.lastBeginFrameTimeStamp = 0;

    // type of rendering being done.
    this.renderType = RENDERMODE_FULL;

    // @see Viewer3DImpl.setProgressiveHaltFrame
    this.progressiveHaltFrame = 0;

    // First frame budget
    // If it's progressive and the first frame, try to finish the ground shadow in the allocated time
    this.INITIAL_GROUND_SHADOW = 0.2;

    // Internal command iterator state
    // is there a command list to execute?
    this.cmdListActive = false;
    // what command we are executing
    this.cmdIndex = 0;
    // are there "CMD_ALWAYS_DO" commands in the command list? If so, we need to traverse the whole command list each tick.
    this.encounteredAlwaysDo = false;
    // did the full render finish? If not, then ignore overlay and present updates until it has
    this.finishedFullRender = true;
    // did the ground shadow get computed in the post-process for deferred rendering?
    this.groundShadowInPost = false;
    // did any previous or current frame trigger the overlay to be drawn?
    this.drawOverlay = false;

    // true means parameters can be set on the command
    this.cmdIsOpen = false;

    // how long the array is (so that if new commands/params are needed, they get allocated first).
    this.allocArraySize = 0;
    // how many commands are in the active command list
    this.cmdListLength = 0;
    // the command and parameters set for the command
    this.cmdList = [];
    this.paramList = [];

    this.bindCommands();
  }


  bindCommands() {
    this.cmdBeginScene = cmdBeginScene.bind(this);
    this.cmdPostAndPresent = cmdPostAndPresent.bind(this);
    this.cmdBeginPhase = cmdBeginPhase.bind(this);
    this.cmdMainRender = cmdMainRender.bind(this);
    this.cmdRenderOverlays = cmdRenderOverlays.bind(this);
    this.cmdFinishAllRendering = cmdFinishAllRendering.bind(this);
    this.cmdForcePresent = cmdForcePresent.bind(this);
    this.cmdSuppressAO = cmdSuppressAO.bind(this);
    this.cmdRestoreAO = cmdRestoreAO.bind(this);
    this.cmdSceneAfterRender = cmdSceneAfterRender.bind(this);
    this.cmdGenerateGroundShadow = cmdGenerateGroundShadow.bind(this);
    this.cmdBlitGroundShadow = cmdBlitGroundShadow.bind(this);
    this.cmdSignalProcessingDone = cmdSignalProcessingDone.bind(this);
    this.cmdSignalRedraw = cmdSignalRedraw.bind(this);
    this.cmdUpdateShadowMap = cmdUpdateShadowMap.bind(this);
    this.cmdResetShadowMap = cmdResetShadowMap.bind(this);
    this.cmdFinishedFullRender = cmdFinishedFullRender.bind(this);
    this.cmdGenerateGroundReflection = cmdGenerateGroundReflection.bind(this);
  }

  isActive() {
    return this.cmdListActive;
  }

  setFrame(timeBudget) {
    this.frameBudget = timeBudget;
  }

  // signal the beginning of a new set of commands
  beginCommandSet() {
    this.cmdListActive = true;
    this.cmdIndex = 0;
    this.cmdListLength = 0;
    this.encounteredAlwaysDo = false;
    this.tickCount = 0;
    this.screenDrawn = NOTHING_DRAWN;
    this.screenHasChanged = false;
  }

  // signal the end
  endCommandSet() {
    if (this.cmdIsOpen) {
      this.cmdIsOpen = false;
      // close previous command - really, increment just to get the final count
      this.cmdListLength++;
    }
  }

  addCommand(func, executionLevel) {
    if (this.cmdIsOpen) {
      // close previous command
      this.cmdListLength++;
    }
    this.cmdIsOpen = true;
    while (this.allocArraySize <= this.cmdListLength) {
      this.cmdList[this.cmdListLength] = {};
      this.paramList[this.cmdListLength] = {};
      this.allocArraySize++;
    }
    this.cmdList[this.cmdListLength] = func;
    this.paramList[this.cmdListLength].executionLevel = executionLevel || CMD_NORMAL_SEQUENCE;
    this.encounteredAlwaysDo = this.encounteredAlwaysDo || executionLevel === CMD_ALWAYS_DO;

    // return value so if we want to jump to this command, we know where to go.
    return this.cmdListLength;
  }

  // note that we're a bit sloppy with parameter setting. Since the parameter set at an index location
  // gets reused, you may see parameters in the parameter object that have nothing to do with this
  // command, since this parameter set might have been used for another command at some other time.
  // Basically, if a command doesn't use the parameter, then ignore it.
  setParam(indexString, val) {
    if (this.cmdIsOpen) {
      this.paramList[this.cmdListLength][indexString] = val;
    } else {
      if (ENABLE_DEBUG) {
        logger.error("ERROR: cannot set param when no command is open!");
      }
    }
  }

  // This method is meant for use during execution of a command, so gets the parameter from the currently-active command.
  getParam(indexString) {
    if (ENABLE_DEBUG) {
      if (this.paramList[this.cmdIndex][indexString] === undefined) {
        logger.error("ERROR: parameter " + indexString + " was never set for this command! Go fix it.");
      }
    }
    return this.paramList[this.cmdIndex][indexString];
  }

  // return true if done running all commands.
  executeCommandList() {

    if (!this.cmdListActive) {
      // If we finish the rendering, then turn
      // off the average frame calculation in cmdBeginScene.
      this.lastBeginFrameTimeStamp = 0;

      // not active, so "done"
      return true;
    }

    // go through command list, interrupting as needed.

    // If we do more than one tick for the rendering, then turn
    // off the average frame calculation in cmdBeginScene.
    if (this.tickCount > 0)
    this.lastBeginFrameTimeStamp = 0;

    // set frame budget
    this.frameRemaining = this.frameBudget;

    if (ENABLE_DEBUG) {
      // reality check
      if (this.cmdIsOpen) {
        logger.error("ERROR: should call endCommandSet before executing");
      }
    }

    let restartIdx;
    // not at end of command list? We always go through the whole command list, as there may be "always do"
    // commands, such as a Present().

    if (ENABLE_DEBUG_RCS) {
      if (this.tickCount === 0) {console.log("===================");}
      console.log("Running commands for " + (this.renderType === RENDERMODE_PROGRESSIVE ? "progressive" :
      this.renderType === RENDERMODE_FULL ? "full" : "silent") +
      " render, for tick count " + this.tickCount);
    }

    // Are there any "always do" commands in this command set, that must be done before we continue our command sequence?
    // Currently needed by smooth navigation, to turn off AO during the render sequence.
    if (this.encounteredAlwaysDo) {
      let idx = 0;
      while (idx < this.cmdIndex) {
        // Is this a command we should always do?
        if (this.paramList[idx].executionLevel >= CMD_ALWAYS_DO) {
          // Commands we always do are assumed to never abort, so we don't check for failure.
          if (ENABLE_DEBUG_RCS) {console.log("  ALWAYS DO command " + idx + ": " + this.cmdList[idx].name);}
          this.cmdList[idx]();
        }
        idx++;
      }
    }

    let interrupted = false;
    while (this.cmdIndex < this.cmdListLength) {
      // "always do after" is for commands that should always execute, e.g., Present()
      const alwaysDoAfter = this.paramList[this.cmdIndex].executionLevel >= CMD_DO_AFTER;
      if (!interrupted || alwaysDoAfter) {
        ENABLE_DEBUG_RCS && console.log(`  command ${this.cmdIndex}: ${this.cmdList[this.cmdIndex].name} and ${this.frameRemaining} frame budget remaining`);

        // true means stop executing, out of time (typically), so restart execution at this command the next tick()
        // signal to not execute any "normal sequence" commands for the rest of the command list.
        const requestInterrupt = Boolean(this.cmdList[this.cmdIndex]()); // can be true, false, and undefined
        interrupted ||= requestInterrupt; // note: once interrupted, we need to keep it truthy

        if (requestInterrupt) {
          ENABLE_DEBUG_RCS && console.log(`  >>> out of tick time with ${this.frameRemaining}`);
          restartIdx = this.cmdIndex;
        }
      }
      // Go to next command until we hit the end of the list; we always continue, since there could be
      // "always do" or "do after" commands in the list that need to be executed.
      this.cmdIndex++;
    }

    // How much of our frame budget is already used up?
    // Note: this.frameRemaining can be negative
    this._usedFrameTime = this.frameBudget - this.frameRemaining;

    if (interrupted) {
      this.cmdIndex = restartIdx; // set where to continue the work next tick()
    } else {
      this.cmdListActive = false; // executed all commands, so we're done
    }
    this.tickCount++;
    return interrupted;
  }

  /**
   * Updates the target frame budget
   *
   * When using the OutOfCoreTileManager, the frame budget is updated based on the frame time scaling factor
   * and frame time fraction. New values for these parameters are provided when this function is invoked
   * by the RenderBudgetManager. If no new values are given, the previous ones will be used.
   *
   * @param {number} [newScalingFactor] - New scaling factor (ratio of CPU and GPU time, as computed by the RenderBudgetManager)
   * @param {number} [newFrameTimeFraction] - New frame time fraction (the fraction of the whole frame time that may be used by this viewer instance)
   * @private
   */
  updateTargetFrameBudget(newScalingFactor, newFrameTimeFraction) {
    if (USE_OUT_OF_CORE_TILE_MANAGER) {

      // Set new scaling factor and frame faction if supplied
      this.frameTimeScalingFactorAvg = newScalingFactor ?? this.frameTimeScalingFactorAvg;
      this.frameTimeFraction = newFrameTimeFraction ?? this.frameTimeFraction;

      // Update the frame budget
      this.targetFrameBudget = this.frameTimeScalingFactorAvg * this.TARGET_FRAME_TIME * this.frameTimeFraction;

    } else {
      //Adjust frame time allowance based on actual frame rate,
      //but stay within the given boundaries.
      if (this.beginFrameAvg < this.TARGET_FRAME_TIME && this.frameBudget < this.MAX_FRAME_BUDGET) {
        this.targetFrameBudget += 1;
      } else
      if (this.beginFrameAvg > this.TARGET_FRAME_TIME && this.frameBudget > this.MIN_FRAME_BUDGET) {
        this.targetFrameBudget *= 0.75 + 0.25 * this.TARGET_FRAME_TIME / this.beginFrameAvg;
      }
    }

    // Clamp to min and max values
    this.targetFrameBudget = Math.min(Math.max(this.targetFrameBudget, this.MIN_FRAME_BUDGET), this.MAX_FRAME_BUDGET);
  }

  setFPSTargets(min, target, max) {
    this.MAX_FRAME_BUDGET = 1000 / max;
    this.MIN_FRAME_BUDGET = 0.1; // (0.1 corresponds to at least a single batch submitted as fast as possible, i.e. the clamped minimum time)
    this.TARGET_FRAME_TIME = 1000 / target;

    this.updateTargetFrameBudget();
  }

}